// API Configuration with Multi-Session Support

import { getCurrentToken } from './SessionManager';

export const getApiUrl = () => {
  // Check if runtime config exists (set by env-config.sh)
  if (window._env_ && window._env_.REACT_APP_API_URL) {
    return window._env_.REACT_APP_API_URL;
  }
  // Fallback to build-time environment variable
  return process.env.REACT_APP_API_URL || 'http://localhost:8000';
};

const API_CONFIG = {
  baseURL: getApiUrl(),
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 30000,
  headers: {
    'Content-Type': 'application/json',
  }
};

// API URL builder
export const buildApiUrl = (endpoint) => {
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${API_CONFIG.baseURL}${cleanEndpoint}`;
};

// Common API call function - returns the Response object
export const apiCall = async (endpoint, options = {}) => {
  const url = buildApiUrl(endpoint);
  
  // CHANGED: Get token for current tab's session
  const token = getCurrentToken();

  // For file uploads, don't set Content-Type (let browser set it with boundary)
  if (options.body instanceof FormData && options.headers) {
    delete options.headers['Content-Type'];
  }

  const defaultOptions = {
    timeout: 30 * 60 * 1000, // 30 minute timeout for large operations
    ...options
  };
  
  const config = {
    headers: {
      ...API_CONFIG.headers,
      ...defaultOptions.headers
    },
    ...defaultOptions
  };

  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout || API_CONFIG.timeout);
    
    const response = await fetch(url, {
      ...config,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    // Check for 401/403 and dispatch global unauthorized event
    if (response.status === 401 || response.status === 403) {
      window.dispatchEvent(new CustomEvent('unauthorized', { 
        detail: { status: response.status, url: endpoint } 
      }));
    }
    
    // Return the Response object directly so Login.js can check response.ok
    return response;
    
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('Request timeout');
    }
    console.error(`API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// Helper function for direct JSON responses (for other parts of your app)
export const apiCallJson = async (endpoint, options = {}) => {
  const response = await apiCall(endpoint, options);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }
  
  const contentType = response.headers.get('content-type');
  if (contentType && contentType.includes('application/json')) {
    return await response.json();
  }
  
  return await response.text();
};

export default API_CONFIG;